/* eslint-disable import/no-cycle */
import React from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { withTheme } from 'styled-components'

import { fetchSuggestions } from 'actions/suggestions'
import Autocomplete from 'components/autocomplete'
import { Label } from 'components/forms/components/styled'
import Icon, { IconNames } from 'components/icons'
import Input from 'components/input'
import { ROUTES_MAX_NUMBER, ROUTE_FILTERS } from 'constants/corporateAgreements'
import e2eClasses from 'helpers/automation'
import { isEmpty } from 'helpers/utils'
import { withErrorClassName } from 'hoc/withErrorClassName'
import NAMESPACES from 'localization/constants/namespaces'
import {
  getSuggestionsFrom as suggestionsFromSelector,
  getSuggestionsTo as suggestionsToSelector
} from 'selectors/suggestions'

import { InputContainer, InputRow } from '../styled'
import { ids } from './ids'
import {
  SectionContainer,
  TitleWrapper,
  SectionTitle,
  IconWrapper,
  FormInformation,
  RowActionWrapper,
  RowActionText,
  InputDecorator
} from './styled'

const InputWithErrorClassName = withErrorClassName(Input)
const AutocompleteWithErrorClassName = withErrorClassName(Autocomplete)

const parseToSuggestionIndex = (entryIndex, rowIndex) => `${entryIndex}-${rowIndex}`
const getIconPadding = () => '9px 0 0 9px'

const TravelsDataFields = ({
  formKey,
  theme,
  formikBag: { setFieldValue, setFieldTouched, handleChange, handleBlur },
  sectionProps: { values = {}, touched = {}, errors = {} },
  ...restProps
}) => {
  const getFieldError = (field, entryKey, rowIndex) =>
    errors[entryKey] && errors[entryKey][rowIndex] && errors[entryKey][rowIndex][field]

  const getFieldTouched = (field, entryKey, rowIndex) =>
    touched[entryKey] && touched[entryKey][rowIndex] && touched[entryKey][rowIndex][field]

  const addRow = (formKey, entryKey) => {
    setFieldValue(`${formKey}.${entryKey}`, [
      ...values[entryKey],
      { origin: '', destination: '', anualSpending: '' }
    ])
  }
  const removeRow = (formKey, entryKey, toDeleteIndex) => {
    setFieldValue(
      `${formKey}.${entryKey}`,
      values[entryKey].filter((_, index) => index !== toDeleteIndex),
      true
    )
    setFieldTouched(`${formKey}.${entryKey}`, false)
  }

  //Exclude filters unsupported by api endpoint
  const filterSuggestions = (suggestions, sibling, baseOnSiblingExclude) =>
    !isEmpty(baseOnSiblingExclude)
      ? suggestions.filter(item => {
          const validations = baseOnSiblingExclude.map(
            excludeProp => item[excludeProp] !== sibling[excludeProp]
          )
          return validations.every(value => value)
        })
      : suggestions

  const filterSiblingSuggestion = (siblingId, baseSuggestions) =>
    siblingId ? baseSuggestions.filter(item => siblingId !== item.id) : baseSuggestions

  const getSuggestions = (
    { siblingName, entryKey, entryIndex, rowIndex, suggestionsGroup },
    { baseOnSiblingExclude = [] }
  ) => {
    const baseSuggestions =
      (restProps[suggestionsGroup] &&
        restProps[suggestionsGroup][parseToSuggestionIndex(entryIndex, rowIndex)]) ||
      []
    const sibling = values[entryKey][rowIndex][siblingName]
    const withoutSelectedSibling = filterSiblingSuggestion(sibling?.id, baseSuggestions)

    return filterSuggestions(withoutSelectedSibling, sibling, baseOnSiblingExclude)
  }

  //Supported filters by api endpoint (countries and regions)
  const onSuggestionsFetchRequested = (
    indexKey,
    suggestionGroup,
    { countries, regions }
  ) => suggestionEvent => {
    if (suggestionEvent.reason === 'input-changed' && suggestionEvent.value.length >= 3) {
      restProps.fetchSuggestions(
        suggestionEvent.value,
        indexKey,
        suggestionGroup,
        countries,
        regions
      )
    }
  }
  const onSuggestionSelected = fieldName => suggestion => {
    setFieldValue(fieldName, suggestion)
  }
  const onChange = fieldName => () => {
    setFieldValue(fieldName, '', true)
    setFieldTouched(fieldName, true)
  }

  const { t } = useTranslation(NAMESPACES.CORPORATE_AGREEMENTS)

  return (
    <SectionContainer isLast noMargin>
      <TitleWrapper e2eClass={e2eClasses('corporate.travelTitle')}>
        <IconWrapper>
          <Icon name={IconNames.Airplane} color={theme.colors.blue} size={24} />
        </IconWrapper>
        <SectionTitle>{t('agreements.request.travels-title')}</SectionTitle>
      </TitleWrapper>
      <FormInformation>{t('agreements.request.travels.helper-information')}</FormInformation>
      {Object.entries(values).map(([entryKey, entryValues], entryIndex) => (
        <React.Fragment key={`${entryKey}--${entryIndex}`}>
          <TitleWrapper e2eClass={`${e2eClasses('corporate.market')}${entryIndex}`}>
            <SectionTitle noPadding>
              {t(`agreements.request.travels.${entryKey}-title`)}
            </SectionTitle>
          </TitleWrapper>
          {entryValues.map((item, rowIndex) => {
            const originError = getFieldError('origin', entryKey, rowIndex)
            const destinationError = getFieldError('destination', entryKey, rowIndex)
            const anualSpendingError = getFieldError('anualSpending', entryKey, rowIndex)
            const originTouched = getFieldTouched('origin', entryKey, rowIndex)
            const destinationTouched = getFieldTouched('destination', entryKey, rowIndex)
            const anualSpendingTouched = getFieldTouched('anualSpending', entryKey, rowIndex)
            const showActionButton = rowIndex || entryValues.length < ROUTES_MAX_NUMBER
            const isAddActionType = !rowIndex

            return (
              <InputRow
                e2eClass={`${e2eClasses('corporate.marketBox')}${entryIndex}`}
                key={`${entryKey}--${rowIndex}--${item.origin?.id}--${item.destination?.id}`}
                isLast={entryValues.length - 1 === rowIndex}
              >
                <InputContainer e2eClass={e2eClasses('searchBox.tOrigin')} flexDirection="column">
                  <Label
                    e2eClass={e2eClasses('searchBox.tOrigin')}
                    fontSize="16px"
                    forId={`${ids.flightOrigin}-${entryIndex}`}
                  >
                    {t('agreements.request.travels.origin.label')}
                  </Label>
                  <AutocompleteWithErrorClassName
                    flatStyle
                    placeholder={t('agreements.request.travels.origin.placeholder')}
                    e2eClass={e2eClasses('searchBox.tOrigin')}
                    error={originTouched && originError}
                    value={item.origin}
                    suggestions={getSuggestions(
                      {
                        siblingName: 'destination',
                        suggestionsGroup: 'suggestionsFrom',
                        entryKey,
                        entryIndex,
                        rowIndex
                      },
                      ROUTE_FILTERS[entryKey]
                    )}
                    onSuggestionSelected={onSuggestionSelected(
                      `travelsFields.${entryKey}[${rowIndex}].origin`
                    )}
                    onSuggestionsFetchRequested={onSuggestionsFetchRequested(
                      parseToSuggestionIndex(entryIndex, rowIndex),
                      'suggestionsFrom',
                      ROUTE_FILTERS[entryKey]
                    )}
                    shouldRenderSuggestions
                    onChange={onChange(`travelsFields.${entryKey}[${rowIndex}].origin`)}
                    onBlur={() => setFieldTouched(`travelsFields.${entryKey}[${rowIndex}].origin`)}
                    id={`${ids.flightOrigin}-${entryIndex}`}
                  />
                </InputContainer>
                <InputContainer e2eClass={e2eClasses('searchBox.tDestiny')} flexDirection="column">
                  <Label
                    e2eClass={e2eClasses('searchBox.tDestiny')}
                    fontSize="16px"
                    forId={`${ids.flightDestination}-${entryIndex}`}
                  >
                    {t('agreements.request.travels.destination.label')}
                  </Label>
                  <AutocompleteWithErrorClassName
                    flatStyle
                    e2eClass={e2eClasses('searchBox.tDestiny')}
                    placeholder={t('agreements.request.travels.destination.placeholder')}
                    name={`travelsFields.${entryKey}[${rowIndex}].destination`}
                    error={destinationTouched && destinationError}
                    value={item.destination}
                    shouldRenderSuggestions
                    suggestions={getSuggestions(
                      {
                        siblingName: 'origin',
                        suggestionsGroup: 'suggestionsTo',
                        entryKey,
                        entryIndex,
                        rowIndex
                      },
                      ROUTE_FILTERS[entryKey]
                    )}
                    onChange={onChange(`travelsFields.${entryKey}[${rowIndex}].destination`)}
                    onBlur={() =>
                      setFieldTouched(`travelsFields.${entryKey}[${rowIndex}].destination`)
                    }
                    onSuggestionSelected={onSuggestionSelected(
                      `travelsFields.${entryKey}[${rowIndex}].destination`
                    )}
                    onSuggestionsFetchRequested={onSuggestionsFetchRequested(
                      parseToSuggestionIndex(entryIndex, rowIndex),
                      'suggestionsTo',
                      ROUTE_FILTERS[entryKey]
                    )}
                    id={`${ids.flightDestination}-${entryIndex}`}
                  />
                </InputContainer>
                <InputContainer justifyContent="space-between" flexWrap="wrap">
                  <InputContainer e2eClass={e2eClasses('corporate.anualSpending')} flex={0.55}>
                    <InputWithErrorClassName
                      e2eClass={e2eClasses('corporate.anualSpending')}
                      name={`travelsFields.${entryKey}[${rowIndex}].anualSpending`}
                      labelFontSize="16px"
                      label={t('agreements.request.travels.anual-spending.label')}
                      placeholder={t('agreements.request.travels.anual-spending.label')}
                      value={item.anualSpending}
                      error={anualSpendingTouched && anualSpendingError}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      iconPadding={getIconPadding()}
                      disableBorder
                      leftIcon={
                        <InputDecorator>
                          {t(`agreements.request.travels.${entryKey}-currency`)}
                        </InputDecorator>
                      }
                      id={`${ids.anualSpending}-${entryIndex}`}
                      role="textbox"
                    />
                  </InputContainer>
                  <InputContainer
                    e2eClass={e2eClasses('corporate.addRoute')}
                    flex={0.4}
                    alignItems="flex-end"
                    height="100px"
                  >
                    {showActionButton && (
                      <RowActionWrapper
                        as="button"
                        onClick={
                          rowIndex
                            ? () => removeRow(formKey, entryKey, rowIndex)
                            : () => addRow(formKey, entryKey, rowIndex)
                        }
                      >
                        <IconWrapper>
                          <Icon
                            name={
                              !rowIndex ? IconNames.AddCircleOutline : IconNames.RemoveCircleOutline
                            }
                            color={theme.colors.blue}
                            size={20}
                          />
                        </IconWrapper>
                        <RowActionText e2eClass={e2eClasses('corporate.addRoute')}>
                          {isAddActionType
                            ? t('agreements.request.travels.route-row.add')
                            : t('agreements.request.travels.route-row.remove')}
                        </RowActionText>
                      </RowActionWrapper>
                    )}
                  </InputContainer>
                </InputContainer>
              </InputRow>
            )
          })}
        </React.Fragment>
      ))}
    </SectionContainer>
  )
}

const mapStateToProps = state => ({
  suggestionsFrom: suggestionsFromSelector(state),
  suggestionsTo: suggestionsToSelector(state)
})

const mapDispatchToProps = {
  fetchSuggestions
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withTheme(
    React.memo(
      TravelsDataFields,
      (prevProps, { sectionProps: { values, touched, errors }, suggestionsFrom, suggestionsTo }) =>
        prevProps.sectionProps.values === values &&
        prevProps.sectionProps.touched === touched &&
        prevProps.sectionProps.errors === errors &&
        prevProps.suggestionsFrom === suggestionsFrom &&
        prevProps.suggestionsTo === suggestionsTo
    )
  )
)
